import React from "react";

import {
  AlertTriangle,
  ArrowRight,
  CircleDashed,
  ClipboardCheck,
  FileCheck,
  Loader2,
  Send,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

import { SPONSOR_STEP, TEAM_STEP, pubLabel } from "./flowCopy";
import type {
  AccountPlanStatus,
  Persona,
  ReviewStatus,
  ReviewSummary,
} from "../../Types/AccountPlan";

/**
 * Deleting an upload (VAL-26/28) is switched off everywhere in the UI (Sushant,
 * 2026-10-29: "remove the option of delete altogether ... we are gonna think
 * holistically on how it should work"). The API keeps the endpoint; turning this
 * back on restores every Delete button and "remove it" link.
 */
export const UPLOAD_DELETE_ENABLED = false;

/** Tabs of the Account Plan page. Labels depend on the persona (see STEPS). */
export type PlanTab = "plan" | "viewer" | "sponsor" | "qa" | "publish";

/** Opening the plan chat from somewhere specific (a Q&A card, a briefing section). */
export interface AskSeed {
  prefill: string;
  /** The Q&A card it came from (bq1..bq5): sent as context, not as the question. */
  questionId?: string | null;
  sectionId?: string | null;
  /** Shown on the composer, e.g. "Q2 · Recurring account calls". */
  label?: string | null;
}

/** What every tab gets for moving around the page. */
export interface PlanNav {
  go: (tab: PlanTab, extra?: Record<string, string | null>) => void;
  /** Open the source slides (Confirm plan step) at a slide (current version unless given). */
  openSlide: (slide: number, fileId?: string | null) => void;
  persona: Persona;
  /** Switch persona, e.g. "Preview as sponsor". Only to a role the user holds. */
  setPersona: (p: Persona) => void;
  canAct: (role: Persona) => boolean;
  /** Read-only "what the sponsor sees" (mock: Preview as sponsor), open to any AE/CSM. */
  previewing: boolean;
  previewAsSponsor: () => void;
  endPreview: () => void;
  /** My Accounts → List (mock v4 "Back to My Accounts"). */
  backToAccounts: () => void;
  /** Open "Ask about this plan", optionally seeded. */
  askAboutPlan?: (seed?: AskSeed) => void;
  /** Not on the account: the published briefing only, no slides (board 01, 2026-10-31). */
  reader?: boolean;
}

export const PERSONA_LABEL: Record<Persona, string> = {
  ES: "Executive Sponsor",
  AE: "Account Executive",
  CSM: "Customer Success",
};

export const PERSONA_SHORT: Record<Persona, string> = {
  ES: "Sponsor",
  AE: "AE",
  CSM: "CSM",
};

/**
 * Numbered steps per persona (mock v4 step bar). `qa` is a lens of Review for AE/CSM.
 * Labels come from flowCopy's TEAM_STEP / SPONSOR_STEP only (F-UF-F21).
 */
const teamSteps = (): { tab: PlanTab; label: string }[] => [
  // Steps are verbs (upload-flow Phase 1). Step 2 is Confirm plan (Phase 2): the
  // AE accepts what was read before any briefing is written; the tab key stays
  // `viewer`, since the slides are where the confirm happens.
  { tab: "plan", label: TEAM_STEP.plan },
  { tab: "viewer", label: TEAM_STEP.viewer },
  { tab: "sponsor", label: TEAM_STEP.sponsor },
  { tab: "publish", label: TEAM_STEP.publish },
];
export const STEPS: Record<Persona, { tab: PlanTab; label: string }[]> = {
  ES: [
    { tab: "sponsor", label: SPONSOR_STEP.sponsor },
    { tab: "qa", label: SPONSOR_STEP.qa },
    { tab: "viewer", label: SPONSOR_STEP.viewer },
  ],
  AE: teamSteps(),
  CSM: teamSteps(),
};

/** "Step 2 · Confirm plan": a tab's place and name in this persona's step bar. */
export const stepHeading = (persona: Persona, tab: PlanTab): string | null => {
  const i = STEPS[persona].findIndex(s => s.tab === tab);
  return i < 0 ? null : `Step ${i + 1} · ${STEPS[persona][i].label}`;
};

export const shortDate = (iso?: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "—";

export const daysSince = (iso?: string | null): number | null => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 86400000)) : null;
};

const unit = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/**
 * How long ago, in the unit that fits (product review 2026-11-01): "just now",
 * "12 minutes", "3 hours", "2 days", "3 weeks", "2 months". Null without a date.
 */
export const elapsed = (
  iso?: string | null,
  now: number = Date.now()
): string | null => {
  if (!iso) return null;
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  const mins = Math.max(0, Math.floor(ms / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return unit(mins, "minute");
  const hours = Math.floor(mins / 60);
  if (hours < 24) return unit(hours, "hour");
  const days = Math.floor(hours / 24);
  if (days < 14) return unit(days, "day");
  if (days < 63) return unit(Math.floor(days / 7), "week");
  return unit(Math.floor(days / 30), "month");
};

/** "just now", "12 minutes ago", "3 days ago". */
export const ago = (iso?: string | null, now: number = Date.now()): string => {
  const e = elapsed(iso, now);
  return e === null ? "" : e === "just now" ? e : `${e} ago`;
};

/**
 * "Under review · just now", "Under review for 12 minutes", and from a day on
 * "Under review for 2 days · since Oct 30". `who` adds " by Emily (AE) …".
 */
export const underReviewFor = (
  since?: string | null,
  who?: string,
  now: number = Date.now()
): string => {
  const by = who ? ` by ${who}` : "";
  const e = elapsed(since, now);
  if (e === null) return `Under review${by}`;
  if (e === "just now") return `Under review${by} · just now`;
  const days = Math.floor(
    (now - new Date(since as string).getTime()) / 86400000
  );
  return `Under review${by} for ${e}${days >= 1 ? ` · since ${shortDate(since)}` : ""}`;
};

export const daysLabel = (days: number | null): string =>
  days === null
    ? ""
    : days === 0
      ? "today"
      : days === 1
        ? "1 day ago"
        : `${days} days ago`;

/** Section ids pending for a role, from a review summary. */
export const pendingFor = (
  review: ReviewSummary | null | undefined,
  role: Persona
): string[] =>
  role === "ES" || !review || review.published
    ? []
    : review.progress[role]?.pending || [];

export interface Stage {
  label: string;
  tone: "slate" | "blue" | "violet" | "amber" | "emerald";
  /** Mock v4 stage icon. */
  icon: LucideIcon;
  /** One-line form for the Plans table, where the full label wrapped (UX audit, 28 Oct). */
  short?: string;
}

/** Pipeline stage badge (mock v4 portfolio "Stage" column). The plan page uses planFlow. */
export const stageOf = (
  status: Pick<
    AccountPlanStatus,
    "latest_file" | "viewer_ready" | "review_status" | "progress" | "published"
  > & { current_version?: number | null }
): Stage => {
  if (!status.latest_file)
    return { label: "No plan", tone: "slate", icon: CircleDashed };
  if (!status.viewer_ready) {
    return status.latest_file.status === "failed"
      ? {
          label: "Plan failed to process",
          short: "Plan failed",
          tone: "amber",
          icon: AlertTriangle,
        }
      : status.latest_file.status === "rejected"
        ? {
            label: "Plan needs review",
            short: "Needs review",
            tone: "amber",
            icon: AlertTriangle,
          }
        : status.latest_file.status === "needs_review"
          ? {
              label: "Plan waiting for review",
              short: "Waiting for review",
              tone: "amber",
              icon: AlertTriangle,
            }
          : {
              label: "Reading the plan…",
              short: "Reading…",
              tone: "blue",
              icon: Loader2,
            };
  }
  const rs: ReviewStatus | null = status.review_status;
  // Confirm plan (Phase 2): read, waiting on the AE before anything is written.
  if ((!rs || rs === "none") && status.latest_file.confirmed === false)
    return {
      label: "Plan read · waiting to confirm",
      short: "Waiting to confirm",
      tone: "amber",
      icon: AlertTriangle,
    };
  if (!rs || rs === "none")
    return {
      label: "Plan ingested · draft not generated",
      short: "No draft yet",
      tone: "blue",
      icon: FileCheck,
    };
  if (rs === "published" && status.published)
    return {
      label: `Published ${pubLabel(status.published)} · ${shortDate(status.published.at)}`,
      short: `Published ${pubLabel(status.published)}`,
      tone: "emerald",
      icon: Send,
    };
  if (rs === "draft")
    return { label: "Draft ready", tone: "violet", icon: Sparkles };
  const p = status.progress;
  const verified = p ? p.AE.verified + p.CSM.verified : 0;
  const owned = p ? p.AE.owned + p.CSM.owned : 0;
  return {
    label: `In review · ${verified} of ${owned} verified`,
    short: `In review · ${verified}/${owned}`,
    tone: "amber",
    icon: ClipboardCheck,
  };
};

export const TONE_CLASS: Record<Stage["tone"], string> = {
  slate: "border-gray-200 bg-gray-50 text-gray-600",
  blue: "border-blue-200 bg-blue-50 text-blue-700",
  violet: "border-violet-200 bg-violet-50 text-violet-700",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

export const StageBadge: React.FC<{ stage: Stage; compact?: boolean }> = ({
  stage,
  compact = false,
}) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${compact ? "whitespace-nowrap" : ""} ${TONE_CLASS[stage.tone]}`}
    title={compact && stage.short ? stage.label : undefined}
  >
    <stage.icon
      className={`h-3 w-3 shrink-0 ${stage.icon === Loader2 ? "animate-spin" : ""}`}
      aria-hidden
    />
    {compact ? (stage.short ?? stage.label) : stage.label}
  </span>
);

interface NextStepProps {
  /** Mono label, e.g. "AE next step" or "All caught up". */
  label: string;
  children: React.ReactNode;
  action?: { label: string; onClick: () => void };
  /** Only feeds the header's next-step button; no card on the page. */
  headerOnly?: boolean;
}

export interface NextStepAction {
  label: string;
  onClick: () => void;
}

/**
 * The page header's copy of the current step's next action (product review
 * 2026-11-01): the AE shouldn't have to scroll to the bottom to move on. Every
 * NextStepCard with an action registers it here; the sticky header shows it.
 */
export const NextStepSlot = React.createContext<
  ((action: NextStepAction | null) => void) | null
>(null);

/** The suggested-next-step card at the bottom of each v4 screen. */
export const NextStepCard: React.FC<NextStepProps> = ({
  label,
  children,
  action,
  headerOnly = false,
}) => {
  const register = React.useContext(NextStepSlot);
  // The header calls the latest onClick, but only re-registers when the label
  // changes, so a new onClick each render can't loop the header's state.
  const latest = React.useRef(action);
  latest.current = action;
  const actionLabel = action?.label;
  React.useEffect(() => {
    if (!register || !actionLabel) return undefined;
    register({
      label: actionLabel,
      onClick: () => latest.current?.onClick(),
    });
    return () => register(null);
  }, [register, actionLabel]);
  if (headerOnly) return null;
  return (
    <div className="mt-6 flex flex-col gap-4 rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-white px-5 py-4 sm:flex-row sm:items-center">
      <div className="hidden h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-[#974ae4] to-[#7a2fc4] text-white shadow-[0_4px_12px_-2px_rgba(124,58,237,0.3)] sm:flex">
        <ArrowRight className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-[#8538d4]">
          {label}
        </div>
        <div className="mt-0.5 text-sm font-medium text-indigo-950">
          {children}
        </div>
      </div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#974ae4] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
        >
          <ArrowRight className="h-4 w-4" aria-hidden />
          {action.label}
        </button>
      )}
    </div>
  );
};

/** Non-interactive "coming soon" pill for features the mock defers. */
export const ComingSoon: React.FC<{
  label: string;
  title?: string;
  icon?: LucideIcon;
}> = ({ label, title, icon: Icon }) => (
  <span
    title={title}
    className="inline-flex cursor-default items-center gap-1.5 rounded-full border border-dashed border-gray-300 px-2.5 py-1 text-xs text-gray-500"
  >
    {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
    {label}
    <span className="rounded bg-gray-100 px-1 font-mono text-[9px] font-semibold uppercase tracking-wide">
      Coming soon
    </span>
  </span>
);
